import { GOLDEN_CASES } from './goldens/cases';
import { grammarText } from './reference';

/**
 * The prompt that turns a problem into a figure.
 *
 * ## Why this exists before any model call does
 *
 * Because it is the part that is actually hard, and it is provider-agnostic. Whichever model
 * ends up being called, it needs the same three things: the grammar it must write, worked
 * examples of the exact translation being asked for, and the problem. Building that here means
 * the feature is usable today by copy and paste, and that the eventual in-app call sends the
 * same text rather than a second prompt that drifts from this one.
 *
 * ## Why the examples come from the corpus
 *
 * `GOLDEN_CASES` stores each problem in the words a teacher is handed, beside the script that
 * draws it — which is exactly a worked example, and it was built for this. Writing examples
 * separately would mean two sets of pairs that slowly disagree, and the corpus is the one with
 * tests behind it: every script quoted below is checked, every run, to draw what it claims.
 *
 * ## Why the grammar is generated rather than written out
 *
 * `grammarText` is built from the same table the parser dispatches on, so it cannot describe a
 * statement that does not exist or omit one that does. Handing a model a stale grammar is how
 * you get a script that fails on every line.
 */

/**
 * How many worked examples to include.
 *
 * One per kind of figure, and the kinds are what the corpus is organised by — a model shown
 * three triangles learns that the answer is a triangle. A handful is about as much as is worth
 * spending: the grammar is the instruction, the examples are only there to fix the *shape* of
 * an answer, and past that they stop teaching and start costing.
 *
 * `graph` is here because leaving it out cost more than the tokens it saves. The rules already
 * spend more lines on functions than on anything else, and a rule is what a model skims; the
 * example is what it copies. The same argument is why the pyramid example is the one that
 * carries `over` — see the corpus case of that name.
 */
const KINDS = ['plane', 'circle', 'solid', 'transform', 'graph', 'epure'] as const;

/**
 * Whether a script draws a round solid.
 *
 * `solid` is one kind in the corpus but two languages to write: a cone or a cylinder has a rim
 * and a couple of named centres, while a prism or a pyramid has a list of corners. A model shown
 * only one of them writes `rim` on a cube, or corner names on a cone — both were seen. So the
 * solid kind contributes two examples where the others contribute one.
 */
const isRound = (script: string): boolean => /^\s*(cone|cylinder)\b/m.test(script);

function examples(): string {
  return KINDS.flatMap((kind) => {
    const ofKind = GOLDEN_CASES.filter((golden) => golden.kind === kind);
    const chosen =
      kind === 'solid'
        ? [ofKind.find((golden) => isRound(golden.script)), ofKind.find((g) => !isRound(g.script))]
        : [ofKind[0]];
    return chosen
      .filter((found) => found !== undefined)
      .map((found) => `Đề:\n${found.problem}\n\nHình:\n${found.script.trim()}`);
  }).join('\n\n---\n\n');
}

/**
 * The instructions, in Vietnamese.
 *
 * The problems are Vietnamese and so is the audience, so the request is too — a model asked in
 * one language to read another has one more thing to get wrong, and the failure shows up as a
 * figure that answers a slightly different question.
 */
const TASK = [
  'Bạn là người vẽ hình cho sách bài tập hình học.',
  '',
  'Đọc đề bài dưới đây và viết một script EduSketch vẽ **hình minh hoạ** cho đề đó.',
  '',
  'Quy tắc:',
  '- Chỉ vẽ hình minh hoạ, không viết lời giải và không trả lời câu hỏi của đề.',
  '- **Nhưng phải tính ra vị trí** của những điểm mà chính đề đã xác định. Đề nói "E trên AM,',
  '  F trên D2I sao cho EF vuông góc với trần" thì E và F chỉ có một chỗ để đứng — đặt đại một',
  '  chỗ khác là vẽ ra một hình nói điều đề không nói. Ở ví dụ đó `E = divide A M 1 3` và',
  '  `F = divide D2 I 2 3` cho EF thẳng đứng đúng như đề. Tính toạ độ để đặt điểm là việc vẽ,',
  '  không phải việc giải.',
  '- Chỉ trả về script, không giải thích gì thêm.',
  '- Đặt tên đúng các điểm mà đề bài nhắc tới, và gắn nhãn cho chúng.',
  '- **Đề về hàm số thì vẽ bằng `graph`, không bao giờ ghép `segment`.** Nhận ra ngay khi đề nhắc',
  '  `f(x)`, `y = ...`, một khoảng như [-1;5], bảng biến thiên, nghiệm, cực trị, hay diện tích',
  '  dưới đường cong. Một chuỗi đoạn thẳng nối các chấm **không phải** đồ thị của hàm liên tục,',
  '  và người dùng cũng không sửa lại được. Viết:',
  '    axes 5',
  '    C = graph "x^3-3x" -1 5',
  '    label C "(C)"',
  '  Cái nhận được là **đồ thị sống**: gõ lại công thức, kéo khoảng x, lấy bảng biến thiên từ nó',
  '  đều được ngay trên canvas. Vì vậy hãy dùng `graph` **cả khi** đề chỉ cho hình vẽ mà không',
  '  cho công thức: chọn công thức đơn giản nhất có đúng dáng điệu (đúng số điểm cực trị, đúng',
  '  dấu, đúng các nghiệm đề nêu) và ghi lựa chọn đó vào một dòng `#`. Bậc ba hợp phần lớn.',
  '  **Cứ viết đúng hàm số đề cho.** Khung hình tự khớp theo đường cong — cao thấp do chính',
  '  `f` quyết định — nên không phải hạ hệ số cho vừa cửa sổ nữa. Tiệm cận cũng tự được vẽ',
  '  bằng nét đứt, không cần dựng bằng `line`.',
  '  **Câu hỏi hỏi điểm nào thì `marks` điểm đó, đừng `plot`.** "Tìm giá trị cực đại", "đồ thị',
  '  cắt Ox tại mấy điểm" — người đọc phải đọc được toạ độ ngay trên hình, và `marks` chấm điểm',
  '  rồi ghi toạ độ, tính thẳng từ công thức:',
  '    C = graph "x^2-4x+3" -1 5',
  '    marks C extrema        # hoặc `roots`, `intercept`, hoặc bỏ trống để lấy tất cả',
  '  Thêm `guides` — `marks C extrema guides` — thì thay vì ghi toạ độ cạnh chấm, nó kẻ nét đứt',
  '  sang hai trục và ghi số ngay chỗ chạm. Dùng khi đề bắt **đọc số từ đồ thị** ("dựa vào đồ',
  '  thị, tìm f(2)"): in sẵn toạ độ cạnh chấm là trả lời hộ câu hỏi mà hình vẽ phải đặt ra.',
  '  Tự đạo hàm rồi gõ `plot -1 2` thì con số đó không có gì kiểm chứng với đường cong bên dưới,',
  '  và sai một dấu là hình nói sai — hãy để máy tính phần đó.',
  '  **Đề nhắc "bảng biến thiên" thì thêm `variation C`** — bảng vẽ ngay dưới hình, lấy đúng',
  '  công thức của `C` nên hình và bảng không thể nói khác nhau.',
  '  Nếu đề **chỉ cho bảng**, không cho đồ thị ("cho bảng biến thiên như hình vẽ, tìm số nghiệm',
  '  của f(x) = m") thì phải giấu cả hai thứ, kể cả trục — giấu mỗi đường cong sẽ để lại một cặp',
  '  trục trống rỗng phía trên bảng:',
  '    X = axes 3          # đặt tên cho trục thì mới giấu được',
  '    C = graph "x^3-3x" -2.5 2.5',
  '    variation C',
  '    hide C',
  '    hide X',
  '  **Đề hỏi tiếp tuyến thì `tangent C <hoành độ>`** — "viết phương trình tiếp tuyến của (C)',
  '  tại điểm có hoành độ x₀ = 2" là `t = tangent C 2`. Hệ số góc lấy từ đạo hàm, nên không tự',
  '  tính rồi dựng bằng `line` qua hai điểm. Lưu ý `tangent c A` (tham số là **tên**) vẫn là',
  '  tiếp tuyến của đường tròn kẻ từ điểm ngoài — hai dạng phân biệt bằng chính đối số.',
  '  Công thức viết như trên bảng: `x^2`, `x^3-3x`, `1/x`, `sqrt(x)`, `abs(x)`, `sin(x)`,',
  '  `ln(x)`, `(x+1)/(x-1)`. Chỉ có một biến và nó là `x`. Đừng vẽ trục bằng `line`: `axes` mới',
  '  có mũi tên, vạch chia và số; quên `axes` thì hệ thống tự thêm một cặp cho khớp.',
  '- **Có toạ độ thì dùng `plot`, không dùng `point`.** Hai lệnh này ở hai hệ đơn vị khác nhau:',
  '  `plot` đọc theo đúng vạch chia của `axes`, còn `point` là đơn vị bản vẽ, nhỏ hơn 40 lần.',
  '  "A(1; 2)" là `A = plot 1 2`; viết `point 1 2` thì cả tam giác co lại thành một chấm ở gốc',
  '  toạ độ, nhãn chồng lên nhau, và không có lỗi nào báo. Dùng `plot` cho mọi bài Oxy, và cho',
  '  điểm mà **đề bài tự cho toạ độ**. Còn điểm phải *tính ra* từ hàm số — cực trị, nghiệm,',
  '  giao với hai trục — thì dùng `marks`, đừng tự tính rồi `plot`. Ngược lại, hình hình học',
  '  phẳng thuần tuý (không có `axes`) thì vẫn dùng `point` như cũ.',
  '- **Đề bất phương trình bậc nhất hai ẩn thì dùng `region`, đừng tự tính giao điểm.**',
  '  "Biểu diễn miền nghiệm của hệ ..." viết thẳng thành:',
  '    region "x >= 0" "y >= 0" "2x + y <= 4"',
  '  Mỗi đối số là **một** bất phương trình tuyến tính đặt trong nháy kép; hệ thống tự vẽ các',
  '  đường biên (nét đứt nếu là `<` hay `>`, nét liền nếu `<=` hay `>=`), tự tô miền nghiệm và',
  '  tự thêm trục vừa khung. Tự tìm các đỉnh rồi `plot` với `polygon` thì hình chỉ là *bản vẽ*',
  '  của đáp án, và không có gì đối chiếu các đỉnh đó với hệ ban đầu. Chỉ nhận bậc nhất: viết',
  '  `"x^2 + y <= 1"` sẽ báo lỗi chứ không ra hình sai.',
  '- Với hình khối, dùng `prism`, `pyramid`, `cone`, `cylinder` hoặc `sphere`. Đừng tự đặt toạ độ',
  '  từng đỉnh: các lệnh này tự lo nét khuất, còn toạ độ đặt tay thì không.',
  '- **Đề hình hoạ (đồ thức, hai mặt phẳng hình chiếu) thì dùng `ground` và `epure`**, đừng ghép',
  '  `point` với `segment`. `epure A 60 90 120` dựng luôn cả hai hình chiếu: `A1` phía trên trục',
  '  ở độ cao, `A2` phía dưới ở độ xa, kèm đường dóng nối chúng. Sau đó nối bằng tên hình chiếu:',
  '    ground 420',
  '    epure A 60 90 120',
  '    epure B 240 150 40',
  '    A1B1 = segment A1 B1',
  '    A2B2 = segment A2 B2',
  '  Đề hỏi **vết** thì dùng `vtrace M A B` (vết đứng, độ xa = 0) và `htrace N A B` (vết bằng,',
  '  độ cao = 0) — đừng tự tính rồi đặt `point`. Cả hai vết đều nằm trên chính đường thẳng ấy,',
  '  nên hình sách giáo khoa vẽ là **từ vết tới vết**: `segment M1 N1` và `segment M2 N2` đi qua',
  '  A, B và chạm trục ở hai đầu.',
  '  Đề hỏi **độ dài thật** thì dùng `truelength T A B`: nó dựng tam giác vuông trên hình chiếu',
  '  bằng, đánh dấu góc vuông, và `A2T` chính là độ dài thật — ghi chú lên đoạn đó.',
  '  Ngoài bốn lệnh ấy thì **chưa có gì**: thay mặt phẳng hình chiếu, phép quay, hình chiếu cạnh,',
  '  xét thấy khuất giữa hai đường chéo nhau — chưa có lệnh nào. Đề hỏi những thứ đó thì vẽ phần',
  '  đồ thức dựng được và ghi một dòng `#` nói rõ bước nào chưa vẽ; đặt điểm bừa để giả lập một',
  '  phép dựng là vẽ ra hình sai mà không có gì báo.',
  '- Đề nhắc **mặt cầu** thì phải vẽ mặt cầu: `sphere O at 0 0 size 320` — một tên (tâm) và một',
  '  số (đường kính). Ghi tâm mà không vẽ mặt cầu là hình thiếu đúng cái đề đang hỏi. Mặt cầu',
  '  ngoại tiếp hình lập phương hay hình hộp có tâm trùng tâm khối, nên hai dòng dùng chung',
  '  `at 0 0`, và đường kính là đường chéo của khối.',
  '- Tâm mặt cầu **nằm ở chỗ phải tính ra** thì đặt bằng tên điểm: `at` nhận toạ độ *hoặc* tên',
  '  một điểm đã dựng. Chóp S.ABC có SA ⊥ đáy và ABC vuông tại B thì tâm là trung điểm SC:',
  '    I = midpoint S C',
  '    sphere at I size 283',
  '  Viết vậy thì mặt cầu bám theo khối, và không cần đặt tên riêng cho tâm nữa.',
  '- **Lăng trụ xiên cũng dùng `over`.** `prism` mặc định dựng đứng; `over` đặt đỉnh **đầu tiên',
  '  của mặt trên** lên trên điểm đó. "Hình chiếu vuông góc của A\' lên (ABC) là trung điểm H',
  '  của BC" → `prism A B C . A2 B2 C2 at 0 0 size 231 300 231 over B C` (hai tên = trung điểm',
  '  cạnh giữa chúng). Đừng bao giờ tự đặt toạ độ sáu đỉnh để giả lập khối xiên: làm vậy là mất',
  '  hết vị trí trong không gian, không còn nét khuất, và ký hiệu góc sẽ đo trên trang giấy —',
  '  một dấu ghi "60°" đo ra 130° là lỗi đã xảy ra đúng như thế.',
  '- **Khoảng cách từ điểm đến mặt phẳng thì dựng bằng hai lần `foot`**, đừng bỏ qua và cũng',
  '  đừng ghi chú suông. Không có `foot` xuống mặt phẳng, và không cần có — cách sách giáo khoa',
  '  dựng chính là cách viết ra. Với d(O, (SAB)), O nằm trong đáy và đỉnh đứng trên đáy:',
  '    K = foot O A B',
  '    OK = segment O K',
  '    SK = segment S K',
  '    H = foot O S K',
  '    OH = segment O H',
  '  Rồi `style OH accent` và `text OH "..."`. Phải vẽ cả `OK` và `SK`, nếu không thì lần `foot`',
  '  thứ hai không có gì để bám. Đừng viết "ngôn ngữ không dựng được chỗ này" — dựng được.',
  '- Điểm trên đường tròn đáy lấy bằng `rim`, không phải `point`, và chân đường vuông góc lấy',
  '  bằng `foot` — chỉ hai lệnh này mới biết vị trí trong không gian, nên mới biết nét nào khuất.',
  '- Đừng tự đặt `style ... hidden` cho nét khuất: khối tự biết đường nào bị nó che và tự vẽ nét',
  '  đứt. Đặt tay thì khi kéo hình đi chỗ khác, nét đứt vẫn nằm nguyên chỗ cũ và thành sai.',
  '- Đánh dấu **mọi** góc và độ dài mà đề nêu, kèm chữ: `a = anglemark S A O` rồi `text a "α"`,',
  '  `text SA "a√3"`. Hình không có các ký hiệu này là hình chưa nói được đề bài. Nhưng khối chỉ',
  '  đặt tên **đỉnh**, không đặt tên cạnh: muốn ghi chữ lên AB thì phải `AB = segment A B` trước.',
  '- **`SA ⊥ (ABCD)` là `over A`.** Mặc định của `pyramid` là đỉnh đứng trên **tâm đáy**, nên',
  '  thiếu `over A` thì SA không vuông góc với gì cả: `pyramid S . A B C D at 0 0 size 240 240',
  '  240 over A`. Đây là dạng hình hay gặp nhất, và bỏ sót mệnh đề này là lỗi hay gặp nhất —',
  '  đo trên chính hình đó, góc tại A ra 63,61° thay vì 90°, và SA dài hơn số đã ghi. Cứ thấy',
  '  "SA vuông góc với đáy", "SA ⊥ (ABC)", "SO ⊥ (ABCD)" là đỉnh phải đứng trên đúng chân đó.',
  '  `over` chỉ nhận **tên một đỉnh đáy** và viết ở cuối dòng.',
  '- Rồi mới đánh dấu: `SA ⊥ (ABCD)` cần `anglemark S A B`. Dấu vuông được tính trong không',
  '  gian, nên cứ đánh dù góc chiếu trên trang trông không phải 90°. Nhưng đánh dấu **không**',
  '  làm cho góc vuông — `over` mới làm. Đánh dấu một góc chưa vuông thì nó vẽ ra thành cung',
  '  nhọn bình thường, và hình tự mâu thuẫn với chú thích của chính nó.',
  '- Nhưng ký hiệu góc phải có **hai cạnh được vẽ** thì mới bám vào đâu. Đừng `hide` cái đỉnh rồi',
  '  đánh dấu góc tại đó, và đừng đánh dấu giữa hai đoạn chưa vẽ — dấu sẽ lơ lửng giữa hình.',
  '  Muốn chỉ EF vuông góc với trần thì vẽ hẳn đoạn từ E lên trần rồi đánh dấu với một cạnh của',
  '  trần cũng đang được vẽ.',
  '- `line` là đường **vô hạn**: dựng nó chỉ để lấy giao điểm thì phải `hide` đi ngay sau khi',
  '  `intersect`, rồi vẽ đúng phần cần thấy bằng `segment`. Để nguyên thì nó chạy ngang cả hình.',
  '- Hình chỉ có **mặt phẳng, không có khối** — cả chương quan hệ song song và quan hệ vuông góc —',
  '  thì dùng `plane3`, đừng dùng `polygon`: `P = plane3 0` rồi `Q = plane3 60` cho hai mặt phẳng',
  '  cắt nhau đúng 60°, và `d = meet P Q` cho giao tuyến **tính ra**. Điểm nằm trong mặt phẳng là',
  '  `M = onplane P 60 -40`; nối hai điểm như thế bằng `segment` là được đường thẳng nằm trong',
  '  mặt phẳng. `dihedral P Q` đánh dấu góc giữa chúng.',
  '- Mặt phẳng của một **khối** thì dùng `plane`, đừng dùng `polygon`: `p = plane S A C` cắt ra',
  '  thiết diện đúng và tự cập nhật khi khối xoay, còn `polygon` chỉ là bốn điểm được tô — nó tô',
  '  đẹp y hệt kể cả khi bốn điểm ấy **không đồng phẳng**, tức là hình sai mà trông vẫn đúng.',
  '  `polygon` + `style p plane` chỉ còn dùng cho hình **phẳng thuần tuý**, nơi không có gì để cắt',
  '  và cũng không có không gian nào — chứ không phải cho hình không gian thiếu khối.',
  '  Chỉ tô mặt phẳng mà **câu hỏi đang nói tới**, đừng tô hết mọi mặt phẳng đề nhắc: hai ba',
  '  mảng nền trong một hình thì người đọc không biết nhìn đâu.',
  '- "Mặt phẳng (P) qua AB hợp với (ABCD) một góc 60°" là `q = turn p A B 60`, với `p` là mặt',
  '  phẳng đã dựng và `A B` là hai điểm **nằm trong chính nó**. Đừng tự tính ra ba điểm rồi',
  '  `plane` qua chúng: số 60 phải nằm trong hình thì mới đúng lại được khi khối đổi.',
  '  Không thấy thiết diện thì viết `turn p B A 60` — mỗi góc có hai mặt phẳng, một nằm ngoài khối.',
  '  Khối tròn thì **không** dùng được: nón hay trụ cắt xiên cho một đường conic, hãy dựng thiết',
  '  diện qua hai đường sinh như ví dụ hình nón.',
  '- Muốn đánh dấu góc giữa hai mặt phẳng thì `dihedral p q`, đừng `anglemark` ba điểm tự chọn:',
  '  góc nhị diện là góc giữa hai tia **vuông góc với giao tuyến**, và chọn nhầm cặp tia thì con',
  '  số hiện ra là một số khác hẳn.',
  '- Đáy tam giác mặc định là tam giác đều, đáy bốn đỉnh mặc định là hình chữ nhật. Đề nói hình',
  '  khác thì thêm `angle <đỉnh> <độ>` vào cuối dòng khối — `right B` là cách viết tắt của',
  '  `angle B 90`. Cạnh lùi về đỉnh trước là width, cạnh tiến sang đỉnh sau là depth:',
  '  "đáy vuông tại B, AB = a, BC = a√3" → `size a … a√3 right B`;',
  '  "đáy ABCD là hình thoi cạnh a, góc BAD = 60°" → `size a … a angle A 60`.',
  '- Đáy hình thang thì dùng `trapezoid <đỉnh> <cạnh xa>`: "hình thang vuông tại A và D,',
  '  AB = a, CD = 2a, AD = a" → `size a … a trapezoid A 2a`. Hình thang **cân** thì dùng',
  '  `isosceles` thay cho `trapezoid`. Cạnh song song **gần** là `depth`, cạnh song song **xa**',
  '  là số của mệnh đề, còn `width` là cạnh nằm giữa hai cạnh đó — đừng ghi cạnh xa vào cả hai.',
  '  Hình thang xiên thì thêm số góc thứ ba: `trapezoid A 2a 60`. `isosceles` không nhận góc.',
  '- Đáy hình gì cũng không tả được thì cho thẳng toạ độ từng đỉnh: `base 0 0 240 0 300 150 70 210`.',
  '  Khi đó `size` chỉ còn chiều cao, và đáy phải lồi.',
  '  Mỗi khối chỉ nhận một trong năm mệnh đề `right` / `angle` / `trapezoid` / `isosceles` / `base`.',
  '- Thứ tự là `size width height depth` — **số giữa là chiều cao**. Đề thường cho sàn trước rồi',
  '  chiều cao sau ("sàn 4m x 4m, cao 3,2m"); viết đúng thứ tự đó là đặt chiều cao vào ô depth.',
  '  Phòng đó là `size 200 160 200`, không phải `size 200 200 160`.',
  '- Kích thước chọn sao cho hình dễ đọc, và đúng với đề: đáy hình vuông thì width = depth,',
  '  hình lập phương thì cả ba bằng nhau.',
].join('\n');

/**
 * Everything that does not depend on the problem: the rules, the grammar, the examples.
 *
 * Separated from the problem because it is the *whole* prompt bar the last few lines — measured
 * at 21 900 characters against a problem of a few hundred — and because every provider can be
 * told to keep a prefix it has seen before. Sent as one string it was re-read from scratch on
 * every call.
 *
 * It is also what makes a second, corrective turn cheap: the instructions do not have to be
 * repeated for the model to be shown its own mistakes.
 */
export function figureInstructions(): string {
  return [
    TASK,
    '',
    '=== NGÔN NGỮ ===',
    '',
    grammarText(),
    '',
    '=== VÍ DỤ ===',
    '',
    examples(),
  ].join('\n');
}

/** The turn that asks for a figure: the problem, and nothing else that changes. */
export function figureMessage(problem: string): string {
  return ['=== ĐỀ CẦN VẼ ===', '', problem.trim()].join('\n');
}

/**
 * The turn that asks for a script to be fixed.
 *
 * The parser was built to be iterated against — it collects every bad line with its number
 * rather than throwing on the first, and its own note calls that "the difference between a tool
 * a language model can iterate against and one it cannot". For a long time nothing used it: the
 * endpoint made one call and handed whatever came back to the reader, errors and all.
 *
 * Sending the errors back is also what lets the rules *shrink*. A rule the parser can catch need
 * not be spelled out in advance for every figure — it arrives exactly when it is broken, costs
 * nothing when it is not, and unlike a bullet in a long prompt it cannot be skimmed past.
 */
export function repairMessage(
  problem: string,
  script: string,
  errors: readonly { readonly line: number; readonly message: string }[]
): string {
  return [
    '=== ĐỀ CẦN VẼ ===',
    '',
    problem.trim(),
    '',
    '=== SCRIPT BẠN VỪA VIẾT ===',
    '',
    script.trim(),
    '',
    '=== NHỮNG DÒNG KHÔNG DỰNG ĐƯỢC ===',
    '',
    ...errors.map((error) => `dòng ${String(error.line)}: ${error.message}`),
    '',
    'Sửa đúng những dòng đó rồi trả về **toàn bộ** script. Chỉ script, không giải thích.',
    'Giữ nguyên những dòng đang đúng.',
  ].join('\n');
}

/** The whole thing as one block, for a person copying it into a model by hand. */
export function figurePrompt(problem: string): string {
  return [figureInstructions(), '', figureMessage(problem)].join('\n');
}

/**
 * Whether this text is a problem statement rather than a script.
 *
 * Decided by the parser rather than by looking for Vietnamese words: if *nothing* in the text
 * built, it was never a script, and no amount of fixing lines will make it one. A script with
 * mistakes still builds most of itself, and that case must keep getting line-numbered errors —
 * telling someone their script is prose because they mistyped one keyword would be worse than
 * the wall of errors this replaces.
 */
export function looksLikeAProblem(created: number, errorCount: number): boolean {
  return created === 0 && errorCount > 0;
}
